import React, { useEffect, useState } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Spinner,
} from "reactstrap"
import { withTranslation } from "react-i18next"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import BootstrapTable from "react-bootstrap-table-next"
import { formatDate, highlightText } from "helpers/misc_helpers"
import {
  GetWatchlistEntryById,
  SearchPartnerCockpitParts,
  GetParts,
  SearchDigikeyParts,
  SearchOctopartParts,
} from "helpers/backend_helper"

const DemandDetails = props => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [demandDetails, setDemandDetails] = useState(null)
  const [partnerCockpitResults, setPartnerCockpitResults] = useState([])
  const [chipsConnectResults, setChipsConnectResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState(null)
  const [digikeyResults, setDigikeyResults] = useState([])
  const [digikeyLoading, setDigikeyLoading] = useState(false)
  const [digikeyError, setDigikeyError] = useState(null)
  const [octopartResults, setOctopartResults] = useState([])
  const [octopartLoading, setOctopartLoading] = useState(false)
  const [octopartError, setOctopartError] = useState(null)

  useEffect(() => {
    const fetchDemandDetails = async () => {
      try {
        setLoading(true)
        const response = await GetWatchlistEntryById(id)
        setDemandDetails(response)
        // Start all searches after details are loaded
        if (response?.partNumber) {
          searchPartnerCockpitParts(response.partNumber)
          searchChipsConnectParts(response.partNumber)
          searchDigikeyParts(response.partNumber)
          searchOctopartParts(response.partNumber)
        }
      } catch (error) {
        console.error("Error fetching demand details:", error)
        setError(props.t("Failed to fetch demand details"))
      } finally {
        setLoading(false)
      }
    }

    fetchDemandDetails()
  }, [id])

  const searchPartnerCockpitParts = async partNumber => {
    try {
      setSearchLoading(true)
      setSearchError(null)
      const result = await SearchPartnerCockpitParts(partNumber)
      setPartnerCockpitResults(result)
    } catch (error) {
      console.error("Error searching partner cockpit parts:", error)
      setSearchError(props.t("Failed to fetch partner cockpit results"))
    } finally {
      setSearchLoading(false)
    }
  }

  const searchChipsConnectParts = async partNumber => {
    try {
      const result = await GetParts(partNumber)
      setChipsConnectResults(result)
    } catch (error) {
      console.error("Error searching ChipsConnect parts:", error)
    }
  }

  const searchDigikeyParts = async partNumber => {
    try {
      setDigikeyLoading(true)
      setDigikeyError(null)
      const result = await SearchDigikeyParts(partNumber)
      setDigikeyResults(result)
    } catch (error) {
      console.error("Error searching Digikey parts:", error)
      if (error.response.status === 400 && error.response.data.error) {
        setDigikeyError(error.response.data.error)
      } else {
        setDigikeyError(props.t("Failed to fetch Digikey results"))
      }
    } finally {
      setDigikeyLoading(false)
    }
  }

  const searchOctopartParts = async partNumber => {
    try {
      setOctopartLoading(true)
      setOctopartError(null)
      const result = await SearchOctopartParts(partNumber)
      setOctopartResults(result)
    } catch (error) {
      console.error("Error searching Octopart parts:", error)
      if (error.response?.status === 400 && error.response.data.error) {
        setOctopartError(error.response.data.error)
      } else {
        setOctopartError(props.t("Failed to fetch Octopart results"))
      }
    } finally {
      setOctopartLoading(false)
    }
  }

  const handleViewDetails = part => {
    if (part.type === "loa") {
      navigate(`/partner-cockpit/loa-details/${part.id}`)
    } else {
      navigate(`/part-search/${part.id}`)
    }
  }

  const isSmallScreen = window.innerWidth < 1600

  const generalSearchColumns = [
    {
      key: "type",
      dataField: "type",
      text: "Type",
      sort: true,
      formatter: cell => (
        <Badge
          color={cell === "loa" ? "info" : "success"}
          className="font-size-12"
        >
          {cell.toUpperCase()}
        </Badge>
      ),
    },
    {
      dataField: "partNumber",
      text: "CPN",
      sort: true,
      formatter: cell => (
        <p
          className="mb-0"
          dangerouslySetInnerHTML={highlightText(
            cell,
            demandDetails?.partNumber
          )}
        ></p>
      ),
    },
    {
      dataField: "manufacturer",
      text: "Manufacturer",
      sort: true,
    },
    {
      dataField: "partnerCockpitName",
      text: "Partner Cockpit",
      sort: true,
    },
    {
      dataField: "companyName",
      text: "Company",
      sort: true,
    },
    {
      dataField: "quantity",
      text: "Quantity",
      sort: true,
      formatter: cell => `${cell} pc.`,
    },
    {
      dataField: "price",
      text: "Unit Price",
      sort: true,
      formatter: (cell, row) => (
        <div className="blurred-text-container">
          <div>{cell ? cell.toFixed(5) + " " + row.currency : "-"}</div>
        </div>
      ),
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },

    {
      dataField: "dateCreated",
      text: "Date Created",
      sort: true,
      formatter: cell => formatDate(cell),
      hidden: isSmallScreen,
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (cell, row) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleViewDetails(row)}
        >
          {props.t("View Details")}
        </button>
      ),
    },
  ]

  const chipsConnectColumns = [
    {
      dataField: "partNumber",
      text: "Manufacturer Part no.",
      sort: true,
      formatter: cell => (
        <p
          className="mb-0"
          dangerouslySetInnerHTML={highlightText(
            cell,
            demandDetails?.partNumber
          )}
        ></p>
      ),
    },
    {
      dataField: "manufacturer",
      text: "Manufacturer",
      sort: true,
    },
    {
      dataField: "description",
      text: "Description",
      sort: true,
    },
    {
      dataField: "quantity",
      text: "Quantity",
      sort: true,
      formatter: cell => `${cell} pc.`,
    },
    {
      dataField: "price",
      text: "Unit Price",
      sort: true,
      formatter: (cell, row) => (
        <div className="blurred-text-container">
          <div>{cell ? cell.toFixed(5) + " " + row.currency : "-"}</div>
        </div>
      ),
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (cell, row) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleViewDetails(row)}
        >
          {props.t("View Details")}
        </button>
      ),
    },
  ]

  const digikeyColumns = [
    {
      dataField: "partNumber",
      text: "Digikey Part Number",
      sort: true,
      formatter: cell => (
        <p
          className="mb-0"
          dangerouslySetInnerHTML={highlightText(
            cell,
            demandDetails?.partNumber
          )}
        ></p>
      ),
    },
    {
      dataField: "manufacturer",
      text: "Manufacturer",
      sort: true,
    },
    {
      dataField: "description",
      text: "Description",
      sort: true,
    },
    {
      dataField: "stock",
      text: "Stock",
      sort: true,
      formatter: cell => `${cell} pc.`,
    },
    {
      dataField: "unitPrice",
      text: "Unit Price",
      sort: true,
      formatter: (cell, row) => (
        <div className="blurred-text-container">
          <div>{cell ? cell.toFixed(5) + " " + row.currency : "-"}</div>
        </div>
      ),
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (cell, row) => (
        <a
          href={row.productUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-primary"
        >
          {props.t("View Details")}
        </a>
      ),
    },
  ]

  const octopartColumns = [
    {
      dataField: "partNumber",
      text: "Manufacturer Part Number",
      sort: true,
      formatter: cell => (
        <p
          className="mb-0"
          dangerouslySetInnerHTML={highlightText(
            cell,
            demandDetails?.partNumber
          )}
        ></p>
      ),
    },
    {
      dataField: "manufacturer",
      text: "Manufacturer",
      sort: true,
    },
    {
      dataField: "description",
      text: "Description",
      sort: true,
    },
    {
      dataField: "quantity",
      text: "Quantity",
      sort: true,
      formatter: cell => `${cell} pc.`,
    },
    {
      dataField: "price",
      text: "Unit Price",
      sort: true,
      formatter: (cell, row) => (
        <div className="blurred-text-container">
          <div>{cell ? cell.toFixed(5) + " " + row.currency : "-"}</div>
        </div>
      ),
      headerStyle: _ => ({
        textAlign: "right",
      }),
      align: () => "right",
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (cell, row) => (
        <a
          href={row.productUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-primary"
        >
          {props.t("View Details")}
        </a>
      ),
    },
  ]

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
            <span className="ms-2">{props.t("Loading...")}</span>
          </div>
        </Container>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center text-danger">{error}</div>
        </Container>
      </div>
    )
  }

  if (!demandDetails) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">{props.t("Demand not found")}</div>
        </Container>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={props.t("Partner Cockpit")}
            breadcrumbItem={props.t("Demand Details")}
            showBackButton
          />
          <Row>
            {/* Left Column - Demand Details */}
            <Col lg="4">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">
                    {props.t("Demand Information")}
                  </h4>
                  <div className="table-responsive">
                    <Table className="table-nowrap mb-0">
                      <tbody>
                        <tr>
                          <th scope="row">{props.t("Part Number")}</th>
                          <td>{demandDetails.partNumber}</td>
                        </tr>
                        <tr>
                          <th scope="row">{props.t("Manufacturer")}</th>
                          <td>{demandDetails.manufacturer}</td>
                        </tr>
                        <tr>
                          <th scope="row">{props.t("Description")}</th>
                          <td>{demandDetails.description}</td>
                        </tr>
                        <tr>
                          <th scope="row">{props.t("Required Quantity")}</th>
                          <td>
                            <span className="text-warning">
                              {demandDetails.quantity} pc.
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{props.t("Target Price")}</th>
                          <td>
                            <span className="text-warning">
                              {demandDetails.price?.toFixed(2)}{" "}
                              {demandDetails.currency}
                            </span>
                          </td>
                        </tr>
                        {/* <tr>
                          <th scope="row">{props.t("Partner Cockpit")}</th>
                          <td>{demandDetails.partnerCockpit?.name}</td>
                        </tr> */}
                        <tr>
                          <th scope="row">{props.t("Notes")}</th>
                          <td>{demandDetails.notes}</td>
                        </tr>
                        <tr>
                          <th scope="row">{props.t("Upload Date")}</th>
                          <td>{formatDate(demandDetails.createdAt)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Right Column - Search Results */}
            <Col lg="8">
              {/* Partner Cockpit Results */}
              <Card className="mb-4">
                <CardBody>
                  <h4 className="card-title mb-4">
                    {props.t("Partner Cockpit Results")}
                  </h4>
                  {searchError && (
                    <div className="text-danger mb-3">{searchError}</div>
                  )}
                  <div className="table-responsive">
                    {searchLoading ? (
                      <div className="text-center py-4">
                        <Spinner color="primary" />
                        <span className="ms-2">
                          {props.t("Loading results...")}
                        </span>
                      </div>
                    ) : (
                      <BootstrapTable
                        keyField="id"
                        data={partnerCockpitResults}
                        columns={generalSearchColumns}
                        bordered={false}
                        striped
                        classes="table align-middle table-nowrap"
                        headerWrapperClasses="table-light"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* ChipsConnect Results */}
              <Card className="mb-4">
                <CardBody>
                  <h4 className="card-title mb-4">
                    {props.t("ChipsConnect Results")}
                  </h4>
                  <div className="table-responsive">
                    <BootstrapTable
                      keyField="id"
                      data={chipsConnectResults}
                      columns={chipsConnectColumns}
                      bordered={false}
                      striped
                      classes="table align-middle table-nowrap"
                      headerWrapperClasses="table-light"
                    />
                  </div>
                </CardBody>
              </Card>

              {/* Digikey Results */}
              <Card className="mb-4">
                <CardBody>
                  <h4 className="card-title mb-4">
                    {props.t("Digikey Results")}
                  </h4>
                  {digikeyError && (
                    <div className="text-danger mb-3">{digikeyError}</div>
                  )}
                  <div className="table-responsive">
                    {digikeyLoading ? (
                      <div className="text-center py-4">
                        <Spinner color="primary" />
                        <span className="ms-2">
                          {props.t("Loading Digikey results...")}
                        </span>
                      </div>
                    ) : (
                      <BootstrapTable
                        keyField="partNumber"
                        data={digikeyResults}
                        columns={digikeyColumns}
                        noDataIndication="No results found"
                        bordered={false}
                        striped
                        classes="table align-middle table-nowrap"
                        headerWrapperClasses="table-light"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Octopart Results */}
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">
                    {props.t("Octopart Results")}
                  </h4>
                  {octopartError && (
                    <div className="text-danger mb-3">{octopartError}</div>
                  )}
                  <div className="table-responsive">
                    {octopartLoading ? (
                      <div className="text-center py-4">
                        <Spinner color="primary" />
                        <span className="ms-2">
                          {props.t("Loading Octopart results...")}
                        </span>
                      </div>
                    ) : (
                      <BootstrapTable
                        keyField="id"
                        data={octopartResults}
                        columns={octopartColumns}
                        noDataIndication="No results found"
                        bordered={false}
                        striped
                        classes="table align-middle table-nowrap"
                        headerWrapperClasses="table-light"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(DemandDetails)
